/**
 * useDebouncedValue.js
 *
 * Returns a copy of `value` that only updates after `value` has stopped
 * changing for `delayMs`. Use it to keep an input controlled and responsive
 * while deferring expensive downstream work (network fetches, big recomputes)
 * until the user pauses:
 *
 *   const [search, setSearch] = useState('');
 *   const debouncedSearch = useDebouncedValue(search, 300);
 *   useEffect(() => { fetchResults(debouncedSearch); }, [debouncedSearch]);
 *
 * The pending update is cancelled on unmount, so no state is set on an
 * unmounted component.
 */

import { useState, useEffect } from 'react';

export default function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}
